import RequestManager, { Response } from '../request-manager';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IClientVarsService } from '@src/slide/domain/slide-app/core/interfaces/client-vars-service.interface';
import { ISocket } from '@tencent/slide-sdk-socket';
import { IOnlineApiService } from '@src/slide/domain/slide-app/scenes/online/core/interfaces/online-api-service.interface';

type CMD = 'PRESENT_INFO';

// TODO: 补齐 retcode，目前只是一小部分
export enum RetCode {
    SUCCESS = 0,

    /** 修改标题 - 标题不合规 */
    NON_COMPLIANCE_TITLE = 12000,

    // 编辑态下的远程演示(即跟随)
    /** 该文档已经被会议占用 */
    DOC_HAS_BEEN_USED = 64000,
    /** 演示信息不存在，视为正常 */
    EMPTY_PRESENT_INFO = 64003,
    /** 主讲人为空, 视为正常 */
    HOST_NOT_EXIST = 64004,
}

export enum EditingPresentRole {
    /** 主讲人 */
    host = 'HOST',
    /** 参会者 */
    audience = 'AUDIENCE',
}

/** 业务场景 */
export enum BizType {
    /** 腾讯会议 */
    meeting = 'meeting',
}

/** 主讲人演示消息 公参 */
export interface HostPresentInfoCommon {
    /** globalPadId */
    docid: string;
    /** 业务场景，比如 meeting 腾讯会议 */
    biztype: BizType;
    /** 业务唯一 id */
    bizid: string;
}

/** 主讲人演示消息, 各品类自定义 */
export interface HostPresentInfo extends HostPresentInfoCommon {
    slideId: string;

    /**
     * 主讲人头像;
     * 主讲人信息原本规划应该在 api.getPresentInfo 的 `hostInfo` 字段中
     */
    avatar: string;
}

export interface IEditingPresentConfig {
    /** 业务场景 */
    biztype: BizType;
    /** 业务唯一 id */
    bizid: string;
    initRole: EditingPresentRole;
}

export interface PresentInfo {
    role?: EditingPresentRole;
    /** 主讲人信息 */
    hostInfo?: {
        uid: string;
    };

    /** 主讲人最后一次同步的消息（通过 socket）, 后端只透传 */
    editPos?: HostPresentInfo;
}

/**
 * 编辑态下的远程演示(编辑态跟随)
 * 跟 ppt 演示态(live)类似，即在编辑态中广播主讲人的页面信息给所有参会者，参会者可选择跟随页面或自由编辑
 */
class EditingPresent {
    private requestManager: RequestManager<CMD>;

    private isInited = false;
    /** 业务场景 */
    private biztype: BizType;
    /** 业务唯一 id */
    private bizid: string;

    public constructor(
        private socket: ISocket,
        private clientVarService: IClientVarsService,
        private apiService: IOnlineApiService,
        private editingPresentManager: { onReceivePresentInfo: (hostPresentInfo: HostPresentInfo) => void },
    ) {
        this.requestManager = new RequestManager<CMD>('EditingPresent', this.socket, this.logger);
    }

    private get logger() {
        return SlideDataServices.Infra.Logger();
    }

    public async init({ bizid, biztype, initRole }: IEditingPresentConfig) {
        this.biztype = biztype;
        this.bizid = bizid;

        const { globalPadId = '', tinyid: selfUid = '' } = this.clientVarService.getClientVars();
        this.logger?.info(`[EditingPresent] sdk init, biztype: ${biztype} bizid: ${bizid} docId: ${globalPadId} uid: ${selfUid}`);

        const presentInfo = await this.getPresetInfo(globalPadId, bizid);
        if (presentInfo === null) {
            this.logger?.info('[EditingPresent] get presentInfo failed, roleType is forced to AUDIENCE');
            this.isInited = true;
            return {
                role: EditingPresentRole.audience,
            };
        }

        this.logger?.info(`[EditingPresent] get presentInfo ${JSON.stringify(presentInfo)}`);

        let roleType = initRole;
        if (roleType === EditingPresentRole.host && !presentInfo.hostInfo /** 当前还没有主讲人 */) {
            this.logger?.info('[EditingPresent] setPresentInfo');
            // 调用 cgi 将自己设置为主讲人; dont catch，出错交给外部处理
            await this.apiService.setPresentInfo(globalPadId, bizid, selfUid);
        } else {
            // 根据后端返回的主讲人 id 判定自己的真实角色
            const hostUid = presentInfo.hostInfo?.uid;
            roleType = hostUid && hostUid === selfUid ? EditingPresentRole.host : EditingPresentRole.audience;
        }

        this.logger?.info(`[EditingPresent] user role is ${roleType}`);
        this.isInited = true;
        return {
            ...presentInfo,
            role: roleType,
        };
    }

    /** 主讲人 Host 广播演示消息 */
    public async hostBroadcast(data: Omit<HostPresentInfo, keyof HostPresentInfoCommon>): Promise<Response> {
        if (!this.isInited) throw new Error('not inited');

        const { globalPadId = '' } = this.clientVarService.getClientVars();
        const hostPresentInfo: HostPresentInfo = {
            docid: globalPadId,
            biztype: this.biztype,
            bizid: this.bizid,

            ...data,
        };

        return this.requestManager.send('PRESENT_INFO', hostPresentInfo);
    }

    /**
     * 接收演示消息
     * @returns boolean false 表示消息被丢弃
     */
    public handleEditingPresentNotice(hostPresentInfo: HostPresentInfo): boolean {
        // 目前直接丢掉消息，未来需要考虑暂存消息以防消息遗漏；
        if (!this.isInited) return false;

        if (!this.bizid || hostPresentInfo.bizid !== this.bizid) {
            this.logger?.info(`[EditingPresent] host broadcast bizid [${hostPresentInfo.bizid}] dont match [${this.bizid}]`);
            return false;
        }

        this.logger?.info(`[EditingPresent] host broadcast ${JSON.stringify(hostPresentInfo)}`);
        this.editingPresentManager.onReceivePresentInfo(hostPresentInfo);
        return true;
    }

    private convertPresentInfo(jsonStr: string) {
        const { hostInfo, editPos } = JSON.parse(jsonStr);
        const presentInfo: PresentInfo = { hostInfo };
        if (typeof editPos === 'string') {
            presentInfo.editPos = JSON.parse(editPos);
        }
        return presentInfo;
    }

    private async getPresetInfo(globalPadId: string, bizid: string) {
        try {
            const { data } = await this.apiService.getPresentInfo(globalPadId, bizid);
            if (data.retcode !== RetCode.SUCCESS) {
            // 主讲人不存在，处理为正常数据
                if ([RetCode.HOST_NOT_EXIST, RetCode.EMPTY_PRESENT_INFO].includes(data.retcode)) return {};
                throw `failed with retcode ${data.retcode}`;
            }
            return this.convertPresentInfo(data.result);
        } catch (err) {
            return null;
        }
    }
}

export default EditingPresent;
